import React, { useRef } from 'react';
import { Box, ButtonBase, alpha } from '@mui/material';

export interface PillTabOption {
	value: string;
	label: React.ReactNode;
}

/**
 * Build the stable tab `id` for a given tab value. Tab values are arbitrary
 * strings — several call sites pass route paths (e.g. "/admin/support"), which
 * would embed characters like `/` into the id and break `querySelector('#...')`
 * usage. We replace any character that is unsafe in a CSS/`querySelector`
 * identifier with `-`. Both the tab `id` here AND the panel's `aria-labelledby`
 * (see AdminPageShell) must use this helper so the ARIA reference resolves.
 */
export const pillTabId = (idPrefix: string, value: string): string =>
	`${idPrefix}-tab-${value.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

interface PillTabsProps {
	value: string;
	options: PillTabOption[];
	onChange: (value: string) => void;
	/** `small` for nested/secondary tab strips, `medium` (default) for primary ones. */
	size?: 'small' | 'medium';
	/** Stretch the strip and its pills to fill the available width. */
	fullWidth?: boolean;
	/** Accessible name for the tablist, used when there is no visible label. */
	ariaLabel?: string;
	/**
	 * Opt in to the full WAI-ARIA tabs relationship. When set, each tab gets a
	 * stable `id` (`pillTabId(idPrefix, value)`) and `aria-controls` pointing at
	 * `${idPrefix}-panel`. The consumer must render the panel with
	 * `id={`${idPrefix}-panel`}`, `role="tabpanel"` and
	 * `aria-labelledby={pillTabId(idPrefix, value)}` (use the exported helper so
	 * the id matches even when the value contains unsafe characters).
	 */
	idPrefix?: string;
}

/**
 * A rounded "pill" tab strip — a tinted track holding pills, the active one filled.
 * Shared across the app for both primary and nested (secondary) tab switching.
 *
 * Implements the WAI-ARIA tabs pattern with automatic activation: a roving
 * tabindex keeps a single tab in the page tab order, and the arrow/Home/End keys
 * move focus between tabs and select them as focus lands (the panels here swap
 * cheap UI, so immediate activation is both expected and conformant). Enter/Space
 * (native to `ButtonBase`) also activate the focused tab.
 */
const PillTabs = ({
	value,
	options,
	onChange,
	size = 'medium',
	fullWidth = false,
	ariaLabel,
	idPrefix
}: PillTabsProps) => {
	const padY = size === 'small' ? 0.5 : 0.75;
	const padX = size === 'small' ? 1.5 : 2.25;
	const fontSize = size === 'small' ? '0.78rem' : '0.85rem';

	const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

	// Move focus to the tab at `index` (wrapping) and select it. Selecting as focus
	// lands is "automatic activation" — for these lightweight panels it matches what
	// users expect from arrow keys and avoids a separate Enter/Space step.
	const activateTabAt = (index: number) => {
		const count = options.length;
		if (count === 0) return;
		const next = ((index % count) + count) % count;
		onChange(options[next].value);
		tabRefs.current[next]?.focus();
	};

	// Handle arrow/Home/End on the tablist container rather than on individual
	// tabs. This makes navigation resilient: it fires whenever focus is anywhere
	// inside the strip, not only on the one button that happens to have focus.
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		const current = options.findIndex((option) => option.value === value);
		const index = current === -1 ? 0 : current;
		switch (event.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				event.preventDefault();
				activateTabAt(index + 1);
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				event.preventDefault();
				activateTabAt(index - 1);
				break;
			case 'Home':
				event.preventDefault();
				activateTabAt(0);
				break;
			case 'End':
				event.preventDefault();
				activateTabAt(options.length - 1);
				break;
			default:
				break;
		}
	};

	return (
		<Box
			role="tablist"
			aria-label={ariaLabel}
			aria-orientation="horizontal"
			onKeyDown={handleKeyDown}
			sx={{
				display: fullWidth ? 'flex' : 'inline-flex',
				width: fullWidth ? '100%' : 'auto',
				maxWidth: '100%',
				p: 0.5,
				gap: 0.5,
				borderRadius: 999,
				bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
				overflowX: 'auto',
				'&::-webkit-scrollbar': { display: 'none' },
				scrollbarWidth: 'none'
			}}
		>
			{options.map((option, index) => {
				const selected = option.value === value;
				return (
					<ButtonBase
						key={option.value}
						ref={(node: HTMLButtonElement | null) => {
							tabRefs.current[index] = node;
						}}
						role="tab"
						id={idPrefix ? pillTabId(idPrefix, option.value) : undefined}
						aria-selected={selected}
						aria-controls={idPrefix ? `${idPrefix}-panel` : undefined}
						tabIndex={selected ? 0 : -1}
						onClick={() => {
							onChange(option.value);
							// macOS browsers don't focus a button on click, so arrow-key
							// navigation would otherwise appear dead after a click. Move
							// focus explicitly so click-then-arrow works everywhere.
							tabRefs.current[index]?.focus();
						}}
						sx={{
							flex: fullWidth ? 1 : 'none',
							px: padX,
							py: padY,
							borderRadius: 999,
							fontSize,
							fontWeight: 600,
							lineHeight: 1.4,
							whiteSpace: 'nowrap',
							color: selected ? 'primary.contrastText' : 'text.secondary',
							bgcolor: selected ? 'primary.main' : 'transparent',
							boxShadow: selected ? 2 : 0,
							transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
							'&:hover': {
								bgcolor: selected ? 'primary.main' : (theme) => alpha(theme.palette.primary.main, 0.14),
								color: selected ? 'primary.contrastText' : 'primary.main'
							},
							// Visible keyboard focus indicator (ButtonBase has none by default).
							// Target the native `:focus-visible` pseudo-class — it matches
							// reliably on keyboard focus, whereas MUI's `.Mui-focusVisible`
							// class is applied by a JS heuristic that can miss (e.g. after a
							// click, or on programmatic focus), leaving the ring invisible.
							// The MUI class is kept as a fallback for older browsers.
							'&:focus-visible, &.Mui-focusVisible': {
								outline: (theme) => `2px solid ${theme.palette.primary.main}`,
								outlineOffset: 2
							}
						}}
					>
						{option.label}
					</ButtonBase>
				);
			})}
		</Box>
	);
};

export default PillTabs;
